import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';

export interface WeeklySummaryCard {
  emoji: string;
  icon: string;
  title: string;
  count: number;
  description: string;
  accent: 'blue' | 'teal' | 'amber' | 'violet';
}

export interface WeeklyTimelineItem {
  date: string;
  icon: string;
  title: string;
  description: string;
  tag: string;
}

export interface WeeklyScreenshotCard {
  title: string;
  description: string;
  icon: string;
  gradient: string;
}

@Component({
  selector: 'app-weekly-updates-modal',
  templateUrl: './weekly-updates-modal.component.html',
  styleUrls: ['./weekly-updates-modal.component.scss'],
})
export class WeeklyUpdatesModalComponent {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  readonly weekLabel = 'Jun 10 – Jun 17, 2025';

  readonly summaryCards: WeeklySummaryCard[] = [
    {
      emoji: '✅',
      icon: 'auto_awesome',
      title: 'New Features Added',
      count: 6,
      description: 'Heat plan comparison, daywise averages, and smarter voyage filters.',
      accent: 'teal',
    },
    {
      emoji: '🔧',
      icon: 'tune',
      title: 'Improvements & Enhancements',
      count: 9,
      description: 'Refined table layouts, clearer deviation categories, and UX polish.',
      accent: 'blue',
    },
    {
      emoji: '🐛',
      icon: 'bug_report',
      title: 'Bugs Fixed',
      count: 4,
      description: 'Sticky headers, scroll behavior, and comparison edge cases resolved.',
      accent: 'amber',
    },
    {
      emoji: '📈',
      icon: 'speed',
      title: 'Performance Optimizations',
      count: 3,
      description: 'Faster chart rendering and lighter compare-table load times.',
      accent: 'violet',
    },
  ];

  readonly timelineItems: WeeklyTimelineItem[] = [
    {
      date: 'Jun 17',
      icon: 'compare_arrows',
      title: 'Heat Plan Comparison enhancements',
      description: 'Merged view now supports daywise average temperature deltas across all tanks.',
      tag: 'Feature',
    },
    {
      date: 'Jun 15',
      icon: 'filter_alt',
      title: 'New filters implementation',
      description: 'Vessel, month, and deviation filters with instant table refresh.',
      tag: 'Enhancement',
    },
    {
      date: 'Jun 13',
      icon: 'bolt',
      title: 'Performance improvements',
      description: 'Compare tables load up to 40% faster with optimized data binding.',
      tag: 'Performance',
    },
    {
      date: 'Jun 11',
      icon: 'palette',
      title: 'UI redesign updates',
      description: 'Premium dark-blue glass panels and enterprise-grade typography refresh.',
      tag: 'Design',
    },
    {
      date: 'Jun 10',
      icon: 'build_circle',
      title: 'Bug fixes',
      description: 'Resolved sticky header scroll issues and consumption delta display gaps.',
      tag: 'Fix',
    },
  ];

  readonly screenshotCards: WeeklyScreenshotCard[] = [
    {
      title: 'Unified Compare Table',
      description: 'Side-by-side MR vs HP with deviation categories and daywise averages.',
      icon: 'table_chart',
      gradient: 'linear-gradient(135deg, #1e3a8a 0%, #0ea5e9 100%)',
    },
    {
      title: 'Voyage Monitoring Dashboard',
      description: 'Redesigned KPI strip with bunker consumption precision metrics.',
      icon: 'dashboard',
      gradient: 'linear-gradient(135deg, #312e81 0%, #6366f1 100%)',
    },
    {
      title: 'Heat Plan Graph View',
      description: 'Interactive port tank temperatures with weighted daily averages.',
      icon: 'show_chart',
      gradient: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    },
  ];

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('wum-backdrop')) {
      this.close();
    }
  }
}
